/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { useToast } from '../ui/use-toast';
import { Loader2, Plus, Edit, Trash2 } from 'lucide-react';
import { insightsActions } from '../../actions/insights.action';
import { Insight, INSIGHT_CATEGORIES } from '../../types/insights/insights';

interface SectionDraft {
  heading: string;
  paragraphs: string; // one paragraph per line
}

interface FormState {
  title: string;
  slug: string;
  category: string;
  author: string;
  readMins: number;
  date: string;
  excerpt: string;
  heroImage: string;
  disclaimer: string;
  featured: boolean;
  status: 'draft' | 'published';
  sections: SectionDraft[];
  takeaways: string; // one per line
}

const emptyForm = (): FormState => ({
  title: '',
  slug: '',
  category: 'Market',
  author: 'VESTN Research',
  readMins: 3,
  date: '',
  excerpt: '',
  heroImage: '',
  disclaimer: '',
  featured: false,
  status: 'published',
  sections: [{ heading: '', paragraphs: '' }],
  takeaways: '',
});

const InsightsManagement = () => {
  const [items, setItems] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Insight | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [formLoading, setFormLoading] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [search, setSearch] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await insightsActions.getInsights();
      setItems(res.data || []);
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to fetch insights', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (it: Insight) => {
    setEditing(it);
    setForm({
      title: it.title,
      slug: it.slug,
      category: it.category,
      author: it.author,
      readMins: it.readMins,
      date: it.date ? it.date.slice(0, 10) : '',
      excerpt: it.excerpt || '',
      heroImage: it.heroImage || '',
      disclaimer: it.disclaimer || '',
      featured: !!it.featured,
      status: it.status || 'published',
      sections: (it.sections || []).length
        ? it.sections.map((s) => ({ heading: s.heading, paragraphs: (s.paragraphs || []).join('\n') }))
        : [{ heading: '', paragraphs: '' }],
      takeaways: (it.takeaways || []).join('\n'),
    });
    setShowForm(true);
  };

  const setSection = (i: number, patch: Partial<SectionDraft>) => {
    setForm((f) => {
      const sections = [...f.sections];
      sections[i] = { ...sections[i], ...patch };
      return { ...f, sections };
    });
  };
  const addSection = () =>
    setForm((f) => ({ ...f, sections: [...f.sections, { heading: '', paragraphs: '' }] }));
  const removeSection = (i: number) =>
    setForm((f) => ({ ...f, sections: f.sections.filter((_, idx) => idx !== i) }));

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setFormLoading(true);
      const payload: any = {
        title: form.title,
        category: form.category,
        author: form.author,
        readMins: Number(form.readMins),
        excerpt: form.excerpt,
        heroImage: form.heroImage,
        disclaimer: form.disclaimer,
        featured: form.featured,
        status: form.status,
        sections: form.sections
          .filter((s) => s.heading.trim() || s.paragraphs.trim())
          .map((s) => ({
            heading: s.heading.trim(),
            paragraphs: s.paragraphs.split('\n').map((p) => p.trim()).filter(Boolean),
          })),
        takeaways: form.takeaways.split('\n').map((t) => t.trim()).filter(Boolean),
      };
      if (form.slug.trim()) payload.slug = form.slug.trim();
      if (form.date) payload.date = new Date(form.date).toISOString();

      if (editing) {
        await insightsActions.updateInsight(editing._id, payload);
        toast({ title: 'Success', description: 'Insight updated' });
      } else {
        await insightsActions.addInsight(payload);
        toast({ title: 'Success', description: 'Insight created' });
      }
      setShowForm(false);
      fetchItems();
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to save insight', variant: 'destructive' });
    } finally {
      setFormLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      setDeleteLoading(true);
      await insightsActions.deleteInsight(deleteId);
      toast({ title: 'Success', description: 'Insight deleted' });
      setDeleteId(null);
      fetchItems();
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (it) =>
        it.title.toLowerCase().includes(q) ||
        it.category.toLowerCase().includes(q) ||
        it.author.toLowerCase().includes(q)
    );
  }, [items, search]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Insights</h1>
        <Button onClick={openAdd}>
          <Plus className="h-4 w-4 mr-2" />
          Add Insight
        </Button>
      </div>

      <Input
        placeholder="Search title, category, author"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="md:max-w-sm"
      />

      <Card>
        <CardHeader>
          <CardTitle>Insights ({filtered.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Author</TableHead>
                <TableHead>Featured</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((it) => (
                <TableRow key={it._id}>
                  <TableCell className="font-medium">
                    {it.title}
                    <div className="text-xs text-muted-foreground">/{it.slug}</div>
                  </TableCell>
                  <TableCell>{it.category}</TableCell>
                  <TableCell>{it.author}</TableCell>
                  <TableCell>{it.featured ? 'Yes' : '—'}</TableCell>
                  <TableCell>
                    <span
                      className={`px-2 py-0.5 text-xs rounded ${
                        it.status === 'published'
                          ? 'bg-green-200 text-green-800'
                          : 'bg-gray-300 text-gray-700'
                      }`}
                    >
                      {it.status}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" onClick={() => openEdit(it)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setDeleteId(it._id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                    No insights.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Insight' : 'Add Insight'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitForm} className="space-y-3">
            <div>
              <Label>Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Slug (optional — auto from title)</Label>
                <Input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  placeholder="why-short-duration-treasuries"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INSIGHT_CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Author</Label>
                <Input
                  value={form.author}
                  onChange={(e) => setForm({ ...form, author: e.target.value })}
                />
              </div>
              <div>
                <Label>Read mins</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.readMins}
                  onChange={(e) => setForm({ ...form, readMins: Number(e.target.value) })}
                />
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Featured</Label>
                <Select
                  value={form.featured ? 'yes' : 'no'}
                  onValueChange={(v) => setForm({ ...form, featured: v === 'yes' })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select
                  value={form.status}
                  onValueChange={(v) => setForm({ ...form, status: v as any })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Excerpt</Label>
              <Textarea
                value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              />
            </div>
            <div>
              <Label>Hero image URL (optional)</Label>
              <Input
                value={form.heroImage}
                onChange={(e) => setForm({ ...form, heroImage: e.target.value })}
              />
            </div>

            {/* Sections */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Body sections</Label>
                <Button type="button" variant="outline" size="sm" onClick={addSection}>
                  <Plus className="h-4 w-4 mr-1" /> Section
                </Button>
              </div>
              {form.sections.map((s, i) => (
                <div key={i} className="rounded border p-3 space-y-2">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Section heading"
                      value={s.heading}
                      onChange={(e) => setSection(i, { heading: e.target.value })}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeSection(i)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <Textarea
                    placeholder="Paragraphs — one per line"
                    value={s.paragraphs}
                    onChange={(e) => setSection(i, { paragraphs: e.target.value })}
                    rows={4}
                  />
                </div>
              ))}
            </div>

            <div>
              <Label>Key takeaways (one per line)</Label>
              <Textarea
                value={form.takeaways}
                onChange={(e) => setForm({ ...form, takeaways: e.target.value })}
              />
            </div>
            <div>
              <Label>Disclaimer (optional)</Label>
              <Textarea
                value={form.disclaimer}
                onChange={(e) => setForm({ ...form, disclaimer: e.target.value })}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={formLoading}>
                {formLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : editing ? (
                  'Update'
                ) : (
                  'Create'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          <div className="py-4">Delete this insight?</div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteId(null)} disabled={deleteLoading}>
              No
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={deleteLoading}>
              {deleteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Yes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InsightsManagement;
